// apps/web/src/router.tsx — Fase 01, Plano 39 (NFR-08/NFR-09); Fase 06, Planos 04/05 (D-04..D-08, D-16).
//
// Router próprio, mínimo e declarativo, com navegação CLIENT-SIDE: cliques em
// links internos (`<a href="/...">`) são interceptados globalmente e resolvidos
// via history.pushState, sem recarregar a página. Isso mantém o AppShell montado
// entre telas com shell — a checagem de sessão roda uma única vez e não há mais
// "piscada" a cada troca de menu. Modificadores (Ctrl/Cmd/Shift/Alt), botão do
// meio e alvo _blank continuam abrindo normalmente (nova aba).
import { useCallback, useEffect, useLayoutEffect, useState, type ComponentType, type ReactElement } from 'react';
import { AdminUsersPage } from './pages/admin-users-page';
import { AdminPerfisPage } from './pages/admin-perfis-page';
import { LoginPage } from './pages/login-page';
import { AjudaPage } from './pages/ajuda-page';
import { DefinirSenhaPage } from './pages/definir-senha-page';
import { TracerPage } from './pages/tracer-page';
import { RemessasPage } from './pages/pcp/remessas-page';
import { NovaOpPage } from './pages/pcp/nova-op-page';
import { OrdensProducaoPage } from './pages/pcp/ordens-producao-page';
import { ApontamentoPage } from './pages/coletor/apontamento-page';
import { LeituraPage } from './pages/coletor/leitura-page';
import { FilaPage } from './pages/coletor/fila-page';
import { TintaColetorPage } from './pages/coletor/tinta-page';
import { KanbanTabletPage } from './pages/coletor/kanban-tablet-page';
import { KanbanTabletConfigurarPage } from './pages/coletor/kanban-tablet-configurar-page';
import { NcListPage } from './pages/qualidade/nc-list-page';
import { CertificadoPage } from './pages/qualidade/certificado-page';
import { ConsumoTintaPage } from './pages/estoque/consumo-tinta-page';
import { InventarioDetalhePage } from './pages/estoque/inventario-detalhe-page';
import { InventariosPage } from './pages/estoque/inventarios-page';
import { LocalizacoesPage } from './pages/estoque/localizacoes-page';
import { SaldosPage } from './pages/estoque/saldos-page';
import { AbastecimentoPage } from './pages/estoque/abastecimento-page';
import { PedidosCompraListaPage } from './pages/compras/pedidos-compra-lista-page';
import { PedidoCompraFormPage } from './pages/compras/pedido-compra-form-page';
import { PedidoCompraDetalhePage } from './pages/compras/pedido-compra-detalhe-page';
import { FornecedoresPage } from './pages/compras/fornecedores-page';
import { NotasCompraPage } from './pages/compras/notas-compra-page';
import { ConferenciaNotaPage } from './pages/compras/conferencia-nota-page';
import { ExpedicaoPage } from './pages/expedicao/expedicao-page';
import { G7Page } from './pages/relatorios/g7-page';
import { GanttPage } from './pages/planejamento/gantt-page';
import { PlanejamentoFilaPage } from './pages/planejamento/fila-page';
import { CadastroListPage } from './pages/cadastros/cadastro-list-page';
import { NovoClientePage } from './pages/cadastros/novo-cliente-page';
import { EditarClientePage } from './pages/cadastros/editar-cliente-page';
import { MateriaisMassEditPage } from './pages/cadastros/materiais-mass-edit-page';
import { AcessosPortalPage } from './pages/cadastros/acessos-portal-page';
import { MonitorRealtimePage } from './pages/monitoramento/monitor-page';
import { VisaoGeralProducaoPage } from './pages/monitoramento/visao-geral-page';
import { CartaoControlePage } from './pages/producao/cartao-controle-page';
import { ResumoLotePage } from './pages/producao/resumo-lote-page';
import { RelatoriosIndexPage } from './pages/relatorios/relatorios-index-page';
import { OmieClientesPage } from './pages/pcp/omie-clientes-page';
import { OmiePendenciasPage } from './pages/pcp/omie-pendencias-page';
import { PendenciasCentralPage } from './pages/pcp/pendencias-central-page';
import { RecebimentosCentralPage } from './pages/pcp/recebimentos-central-page';
import { ConferenciaRecebimentoPage } from './pages/pcp/conferencia-recebimento-page';
import { ImportarCargaMaquinaPage } from './pages/pcp/roteiros/importar-cargamaquina-page';
import { ImportarDadosTecnicosPage } from './pages/pcp/roteiros/importar-dados-tecnicos-page';
import { ModelosRoteiroPage } from './pages/pcp/roteiros/modelos-roteiro-page';
import { NovaPecaPage } from './pages/pcp/roteiros/nova-peca-page';
import { PecaCadastroPage, PecaRoteiroPage } from './pages/pcp/roteiros/peca-roteiro-page';
import { PecasProdutosPage, RoteirosListaPage } from './pages/pcp/roteiros/roteiros-lista-page';
import { TintasRoteiroPage } from './pages/pcp/roteiros/tintas-roteiro-page';
import { DashboardPage } from './pages/painel/dashboard-page';
import KanbanPage from './pages/planejamento/kanban-page';
import { PedidosListaPage } from './pages/vendas/pedidos-lista-page';
import { PedidoFormPage } from './pages/vendas/pedido-form-page';
import { PedidoDetalhePage } from './pages/vendas/pedido-detalhe-page';
import { ImportarPage } from './pages/vendas/importar-page';
import { PrecosPage } from './pages/vendas/precos-page';
import { AppShell } from './components/shell/app-shell';
import { GuardaPermissao } from './components/vendas/guarda-permissao';
import { PERMISSAO_ROTA_VENDAS } from './lib/vendas/permissao';
import { PERMISSAO_ROTA_COMPRAS } from './lib/compras/permissao';
import { iniciarAtividadeSessao, notificarSessaoExpirada, deveRedirecionarAoExpirarSessao } from './lib/atividade-sessao';
import { obterCredencial } from './lib/kanban/dispositivo';

export { deveRedirecionarAoExpirarSessao } from './lib/atividade-sessao';

interface AppRoute {
  readonly path: string;
  readonly Component: ComponentType;
  /** true = envolve no AppShell (Gestão/PCP/Qualidade/Admin, D-16). */
  readonly shell: boolean;
}

/**
 * Envolve a página de escrita numa guarda de permissão do cliente (WR-D1-07): quem não pode ver "Sem permissão" antes de
 * preencher o formulário. Criada uma vez por rota, no escopo do módulo, para manter a identidade do componente estável.
 */
function protegida(Pagina: ComponentType, permissao: string, voltar?: { readonly href: string; readonly rotulo: string }): ComponentType {
  return function PaginaProtegida(): ReactElement {
    return (
      <GuardaPermissao permissao={permissao} voltar={voltar}>
        <Pagina />
      </GuardaPermissao>
    );
  };
}

/** A raiz não tem conteúdo próprio: o AppShell redireciona para a página inicial do perfil (destinoInicial). */
function EntradaPage(): null {
  return null;
}

// Rotas COM AppShell (Gestão, PCP, Qualidade, Admin — D-16)
// e rotas SEM shell (Login, Tracer e Coletor — D-03, D-07).
export const routes: readonly AppRoute[] = [
  { path: '/', Component: EntradaPage, shell: true },
  { path: '/painel', Component: DashboardPage, shell: true },
  { path: '/ajuda', Component: AjudaPage, shell: true },
  { path: '/admin/users', Component: AdminUsersPage, shell: true },
  { path: '/admin/perfis', Component: AdminPerfisPage, shell: true },
  { path: '/vendas/pedidos', Component: PedidosListaPage, shell: true },
  { path: '/vendas/pedidos/novo', Component: protegida(PedidoFormPage, PERMISSAO_ROTA_VENDAS['/vendas/pedidos/novo']!), shell: true },
  { path: '/vendas/pedidos/importar', Component: protegida(ImportarPage, PERMISSAO_ROTA_VENDAS['/vendas/pedidos/importar']!), shell: true },
  { path: '/vendas/pedidos/detalhe', Component: PedidoDetalhePage, shell: true },
  { path: '/vendas/precos', Component: protegida(PrecosPage, PERMISSAO_ROTA_VENDAS['/vendas/precos']!), shell: true },
  { path: '/remessas', Component: RemessasPage, shell: true },
  { path: '/expedicao', Component: protegida(ExpedicaoPage, 'expedicao:consultar'), shell: true },
  { path: '/producao/ordens', Component: protegida(OrdensProducaoPage, 'producao:consultar'), shell: true },
  { path: '/producao/ordens/preparar', Component: protegida(NovaOpPage, 'producao:consultar'), shell: true },
  { path: '/producao/ordens/detalhe', Component: protegida(NovaOpPage, 'producao:consultar'), shell: true },
  { path: '/producao/nova-op', Component: protegida(NovaOpPage, 'producao:gerenciar'), shell: true },
  { path: '/producao/cartao-controle', Component: CartaoControlePage, shell: true },
  { path: '/producao/resumo-lote', Component: ResumoLotePage, shell: true },
  { path: '/qualidade/nao-conformidades', Component: NcListPage, shell: true },
  { path: '/qualidade/certificados', Component: CertificadoPage, shell: true },
  { path: '/estoque', Component: SaldosPage, shell: true },
  { path: '/estoque/abastecimento', Component: protegida(AbastecimentoPage, 'estoque:consultar'), shell: true },
  { path: '/estoque/localizacoes', Component: LocalizacoesPage, shell: true },
  { path: '/estoque/inventarios', Component: InventariosPage, shell: true },
  { path: '/estoque/inventarios/detalhe', Component: InventarioDetalhePage, shell: true },
  { path: '/estoque/consumo-tinta', Component: ConsumoTintaPage, shell: true },
  { path: '/compras/pedidos', Component: PedidosCompraListaPage, shell: true },
  {
    path: '/compras/pedidos/novo',
    Component: protegida(PedidoCompraFormPage, PERMISSAO_ROTA_COMPRAS['/compras/pedidos/novo']!, { href: '/compras/pedidos', rotulo: 'Voltar aos pedidos de compra' }),
    shell: true,
  },
  { path: '/compras/pedidos/detalhe', Component: PedidoCompraDetalhePage, shell: true },
  { path: '/compras/notas', Component: NotasCompraPage, shell: true },
  { path: '/compras/notas/conferencia', Component: ConferenciaNotaPage, shell: true },
  { path: '/compras/fornecedores', Component: FornecedoresPage, shell: true },
  { path: '/relatorios/g7', Component: G7Page, shell: true },
  { path: '/planejamento/gantt', Component: GanttPage, shell: true },
  { path: '/planejamento/fila', Component: PlanejamentoFilaPage, shell: true },
  { path: '/planejamento/kanban', Component: KanbanPage, shell: true },
  { path: '/cadastros', Component: CadastroListPage, shell: true },
  { path: '/cadastros/clientes/novo', Component: NovoClientePage, shell: true },
  { path: '/cadastros/clientes/:id/editar', Component: EditarClientePage, shell: true },
  { path: '/cadastros/materiais', Component: MateriaisMassEditPage, shell: true },
  // Fase 17 (D-10, PORT-UI-20): gestão de acessos do portal; a rota exige a permissão dedicada (a API devolve 403 de qualquer forma).
  {
    path: '/cadastros/acessos-portal',
    Component: protegida(AcessosPortalPage, 'portal:gerenciar-acessos', { href: '/cadastros', rotulo: 'Voltar aos cadastros' }),
    shell: true,
  },
  { path: '/monitoramento/painel', Component: MonitorRealtimePage, shell: true },
  { path: '/monitoramento/visao-geral', Component: VisaoGeralProducaoPage, shell: true },
  { path: '/relatorios', Component: RelatoriosIndexPage, shell: true },
  { path: '/omie/clientes', Component: OmieClientesPage, shell: true },
  { path: '/omie/pendencias', Component: OmiePendenciasPage, shell: true },
  // L-63 (17.1-28): a Central de Pendências Operacionais (CANCELAMENTO_NFE, SEM_ROTEIRO...) tinha página e API, mas nenhuma rota.
  // A API (`GET /pendencias`) exige remessas:consultar.
  { path: '/pendencias', Component: protegida(PendenciasCentralPage, 'remessas:consultar'), shell: true },
  { path: '/pcp/recebimentos', Component: protegida(RecebimentosCentralPage, 'recebimentos:consultar'), shell: true },
  { path: '/pcp/recebimentos/:id', Component: protegida(ConferenciaRecebimentoPage, 'recebimentos:consultar'), shell: true },
  { path: '/pcp/roteiros', Component: protegida(RoteirosListaPage, 'producao:consultar'), shell: true },
  { path: '/cadastros/pecas', Component: protegida(PecasProdutosPage, 'producao:consultar'), shell: true },
  { path: '/cadastros/pecas/nova', Component: protegida(NovaPecaPage, 'roteiros:gerenciar'), shell: true },
  { path: '/cadastros/pecas/:id', Component: protegida(PecaCadastroPage, 'producao:consultar'), shell: true },
  { path: '/pcp/roteiros/importar', Component: protegida(ImportarCargaMaquinaPage, 'roteiros:gerenciar'), shell: true },
  { path: '/pcp/roteiros/dados-tecnicos', Component: protegida(ImportarDadosTecnicosPage, 'roteiros:gerenciar'), shell: true },
  { path: '/pcp/roteiros/modelos', Component: protegida(ModelosRoteiroPage, 'producao:consultar'), shell: true },
  { path: '/pcp/roteiros/tintas', Component: protegida(TintasRoteiroPage, 'producao:consultar'), shell: true },
  { path: '/pcp/roteiros/nova', Component: protegida(NovaPecaPage, 'roteiros:gerenciar'), shell: true },
  { path: '/pcp/roteiros/:id', Component: protegida(PecaRoteiroPage, 'producao:consultar'), shell: true },
  { path: '/tracer', Component: TracerPage, shell: false },
  { path: '/login', Component: LoginPage, shell: false },
  { path: '/definir-senha', Component: DefinirSenhaPage, shell: false },
  { path: '/coletor/apontamento', Component: ApontamentoPage, shell: false },
  { path: '/coletor/leitura', Component: LeituraPage, shell: false },
  { path: '/coletor/fila', Component: FilaPage, shell: false },
  { path: '/coletor/tinta', Component: TintaColetorPage, shell: false },
  { path: '/coletor/kanban', Component: KanbanTabletPage, shell: false },
  { path: '/coletor/kanban/configurar', Component: KanbanTabletConfigurarPage, shell: false },
];

const loginRoute = routes.find((r) => r.path === '/login')!;

/** Só a conferência fiscal usa segmento dinâmico; os demais caminhos continuam sendo exatos. */
function encontrarRota(pathname: string): AppRoute | undefined {
  return routes.find((route) => route.path === pathname) ?? routes.find((route) => {
    if (route.path === '/cadastros/clientes/:id/editar') return /^\/cadastros\/clientes\/[^/]+\/editar$/.test(pathname);
    if (!route.path.endsWith('/:id')) return false;
    const prefixo = route.path.slice(0, -4);
    return pathname.startsWith(`${prefixo}/`) && pathname.length > prefixo.length + 1;
  });
}

/** Casa a rota exata; sem match, cai no login (app é login-gated). */
export function resolveRoute(pathname: string): AppRoute {
  return encontrarRota(pathname) ?? loginRoute;
}

/** Resolve o pathname inicial. A raiz entra no shell, que confere a sessão (sem ela, vai ao login) e leva à página inicial. */
function pathnameInicial(): string {
  return window.location.pathname;
}

export function AppRouter(): ReactElement {
  const [pathname, setPathname] = useState<string>(pathnameInicial);
  const [search, setSearch] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.search : '',
  );

  const navigate = useCallback((to: string) => {
    const url = new URL(to, window.location.origin);
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    window.history.pushState(null, '', `${url.pathname}${url.search}`);
    setPathname(url.pathname);
    setSearch(url.search);
    window.scrollTo(0, 0);
  }, []);

  // Botões voltar/avançar do navegador.
  useEffect(() => {
    const onPop = (): void => {
      setPathname(window.location.pathname);
      setSearch(window.location.search);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Intercepta cliques em links internos e navega client-side (sem reload).
  useEffect(() => {
    const onClick = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;
      const target = anchor.getAttribute('target');
      if (target && target !== '_self') return;
      if (anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      // Só caminhos internos absolutos ("/algo") que casam com uma rota conhecida;
      // query string (osId do coletor) precisa sobreviver — sem ela o apontamento
      // perdia a OS identificada.
      if (!href || !href.startsWith('/')) return;
      const url = new URL(href, window.location.origin);
      if (!encontrarRota(url.pathname)) return;
      event.preventDefault();
      navigate(`${url.pathname}${url.search}`);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);

  useLayoutEffect(() => {
    // Isola o acabamento de gestão das telas touch do coletor.
    document.documentElement.toggleAttribute('data-office-theme', !pathname.startsWith('/coletor'));
  }, [pathname]);
  const route = resolveRoute(pathname);
  const exigeSessao = pathname !== '/login' && pathname !== '/definir-senha';
  useEffect(() => {
    if (!exigeSessao) return;
    return iniciarAtividadeSessao(document, () => {
      void obterCredencial()
        .then((cred) => {
          if (deveRedirecionarAoExpirarSessao(pathname, Boolean(cred))) {
            navigate('/login');
          } else {
            notificarSessaoExpirada();
          }
        })
        .catch(() => {
          if (deveRedirecionarAoExpirarSessao(pathname, false)) {
            navigate('/login');
          } else {
            notificarSessaoExpirada();
          }
        });
    });
  }, [exigeSessao, pathname, navigate]);
  const page = <route.Component key={`${pathname}${search}`} />;

  // AppShell fica montado enquanto navegamos entre telas com shell (mesma
  // posição na árvore) — a checagem de sessão não repete e não há piscada.
  return route.shell ? <AppShell pathname={pathname}>{page}</AppShell> : page;
}
